import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldCheck, Lock, Heart, UserPlus, Trash2, Loader2 } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useDataLabel } from '@/lib/dataI18n';
import { api, formatApiError } from '@/lib/api';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';
import { toast } from 'sonner';

/**
 * Issue 3 — End-user profile is strictly READ-ONLY for the CTH-managed fields
 * (name, email, position, company). Voluntary TalentHeart "Conóceme" fields
 * (birth date, hobbies, family info, favorites) are editable by the candidate
 * — they feed the TalentHeart birthday calendar and "Get to know your team"
 * widgets. All fields are OPTIONAL.
 */
function Field({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{value || '—'}</p>
    </div>
  );
}

const EMPTY_CHILD = { age: '', gender: '', interests: '' };

export default function EndUserProfile() {
  const { user, setUser } = useApp();
  const { t } = useTranslation();
  const tData = useDataLabel();
  const initials = (user?.name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const [profile, setProfile] = useState({
    birth_date: '', birthday_share_consent: null, preferences_share_consent: null, celebration_preferences: '', address: '', favorite_color: '', favorite_dessert: '',
    favorite_sport: '', favorite_team: '', favorite_food: '',
    hobbies: [], children: [],
  });
  const [hobbiesText, setHobbiesText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // TC-Closeout FIX 1/2 — resolve the CURRENT evaluation position from the
  // participant's assignments instead of stale request metadata.
  const [currentEval, setCurrentEval] = useState(null);

  // Iter 41.34 — when the user lands here right after consent (intro not yet
  // seen), surface "Continuar a mis evaluaciones" + "Completar más tarde" so
  // the page becomes the on-boarding step. Returning visits just show "Save".
  const isIntro = user?.profile_intro_seen === false;
  const navigate = useNavigate();

  // Iter 41.34 follow-up — small progress bar shown only in intro mode.
  // Counts how many of the 9 logical field groups have at least one value.
  const completedCount = (() => {
    let n = 0;
    if (profile.birth_date)       n++;
    if (profile.address)          n++;
    if (profile.favorite_color)   n++;
    if (profile.favorite_dessert) n++;
    if (profile.favorite_sport)   n++;
    if (profile.favorite_team)    n++;
    if (profile.favorite_food)    n++;
    if (hobbiesText.split(',').map((s) => s.trim()).filter(Boolean).length > 0) n++;
    if (profile.children.some((c) => c.age || c.gender || c.interests)) n++;
    return n;
  })();
  const TOTAL_FIELDS = 9;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/me/assignments');
        if (!alive) return;
        const withPos = (data || [])
          .filter((a) => a.position)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setCurrentEval(withPos[0] || (data || [])[0] || null);
      } catch { /* profile still renders with account data */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/me/personal-profile');
        if (!alive) return;
        const p = {
          birth_date: data.birth_date || '',
          birthday_share_consent: (data.birthday_share_consent === true || data.birthday_share_consent === false)
            ? data.birthday_share_consent : null,
          preferences_share_consent: (data.preferences_share_consent === true || data.preferences_share_consent === false)
            ? data.preferences_share_consent : null,
          celebration_preferences: data.celebration_preferences || '',
          address: data.address || '',
          favorite_color: data.favorite_color || '',
          favorite_dessert: data.favorite_dessert || '',
          favorite_sport: data.favorite_sport || '',
          favorite_team: data.favorite_team || '',
          favorite_food: data.favorite_food || '',
          hobbies: Array.isArray(data.hobbies) ? data.hobbies : [],
          children: Array.isArray(data.children) ? data.children.map((c) => ({
            age: c.age ?? '', gender: c.gender || '', interests: c.interests || '',
          })) : [],
        };
        setProfile(p);
        setHobbiesText((p.hobbies || []).join(', '));
      } catch {
        /* fresh profile, leave defaults */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setChild = (i, k, v) => setProfile((p) => ({
    ...p, children: p.children.map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  }));
  const addChild = () => setProfile((p) => ({ ...p, children: [...p.children, { ...EMPTY_CHILD }] }));
  const removeChild = (i) => setProfile((p) => ({ ...p, children: p.children.filter((_, idx) => idx !== i) }));

  const _persistProfile = async () => {
    const hobbies = hobbiesText.split(',').map((s) => s.trim()).filter(Boolean);
    const payload = {
      ...profile,
      hobbies,
      children: profile.children
        .map((c) => ({
          age: c.age === '' || c.age == null ? null : Number(c.age),
          gender: c.gender || null,
          interests: c.interests || null,
        }))
        .filter((c) => c.age != null || c.gender || c.interests),
    };
    await api.put('/me/personal-profile', payload);
  };

  const _markIntroSeen = async () => {
    try {
      await api.post('/me/profile-intro-seen');
      if (setUser && user) setUser({ ...user, profile_intro_seen: true });
    } catch { /* non-fatal — flag will resolve on next login */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      await _persistProfile();
      toast.success(t('pages.profile.personal.saved', { defaultValue: 'Saved' }));
    } catch (e) {
      toast.error(t('pages.profile.personal.saveFailed', { defaultValue: 'Could not save' }), {
        description: formatApiError(e),
      });
    } finally {
      setSaving(false);
    }
  };

  // Iter 41.34 — onboarding gate: save (or skip) then mark seen + go to /me.
  const continueToEvals = async () => {
    setSaving(true);
    try {
      await _persistProfile();
      await _markIntroSeen();
      toast.success(t('pages.profile.personal.continueToast',
        { defaultValue: 'Listo, vamos a tus evaluaciones' }));
      navigate('/me', { replace: true });
    } catch (e) {
      toast.error(t('pages.profile.personal.saveFailed', { defaultValue: 'Could not save' }), {
        description: formatApiError(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const skipForNow = async () => {
    setSaving(true);
    try {
      await _markIntroSeen();
      navigate('/me', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Topbar title={t('pages.profile.title')} subtitle={t('pages.profile.readOnlySubtitle')} />
      <div className="p-6 lg:p-8 space-y-6">
        {/* ----- read-only CTH-managed fields ----- */}
        <Card className="p-6 lg:p-8 shadow-card max-w-3xl" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border-4 border-background shadow-soft">
              <AvatarFallback className="bg-gradient-primary text-white text-lg font-bold">{user?.avatar || initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h2 className="font-display text-xl font-bold tracking-tight">{user?.name}</h2>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              <Badge variant="outline" className="mt-2 font-medium">{t('pages.profile.evaluatedBadge')}</Badge>
            </div>
          </div>

          <div className="mt-6 p-3 rounded-lg border bg-muted/30 flex items-start gap-2.5" data-testid="profile-readonly-banner">
            <Lock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('pages.profile.readOnlyNotice')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 mt-6" data-testid="profile-fields">
            <Field label={t('pages.profile.fullName')}        value={user?.name} />
            <Field label={t('pages.profile.email')}           value={user?.email} />
            <Field
              label={currentEval?.position
                ? t('pages.profile.positionEvaluated', { defaultValue: 'Posición en evaluación' })
                : t('pages.profile.positionLabel', { defaultValue: 'Posición' })}
              value={tData(currentEval?.position || user?.position)}
            />
            <Field label={t('pages.profile.company')}         value={tData(user?.company_name || currentEval?.company_name)} />
          </div>

          {user?.consent_accepted_at && (
            <div className="mt-6 pt-5 border-t border-border/60 flex items-center gap-2 text-xs text-muted-foreground" data-testid="profile-consent-status">
              <ShieldCheck className="h-4 w-4 text-success" />
              <span>
                {t('pages.profile.consentAcceptedOn', {
                  date: new Date(user.consent_accepted_at).toLocaleDateString(getUiLocale()),
                })}
              </span>
            </div>
          )}
        </Card>

        {/* ----- TalentHeart "Conóceme" editable section ----- */}
        <Card className="p-6 lg:p-8 shadow-card max-w-3xl" data-testid="profile-personal-card">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
              <Heart className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">
                {t('pages.profile.personal.title', { defaultValue: 'Mi Perfil Personal — Conóceme' })}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('pages.profile.personal.subtitle', { defaultValue: 'Estos datos son voluntarios y nos ayudan a personalizar TalentHeart.' })}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : (
            <div className="space-y-6">
              {isIntro && (
                <div className="p-3 rounded-lg border bg-primary/5 border-primary/30 space-y-2"
                     data-testid="pp-intro-banner">
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    {t('pages.profile.personal.introBanner', {
                      defaultValue: 'Antes de empezar tus evaluaciones, dedica un momento a contarnos sobre ti. Todo es opcional — si prefieres, puedes completarlo más tarde desde el botón Profile.',
                    })}
                  </p>
                  <div className="flex items-center gap-3 pt-1" data-testid="pp-progress-row">
                    <Progress value={(completedCount / TOTAL_FIELDS) * 100} className="h-2 flex-1" />
                    <span className="text-xs font-semibold text-muted-foreground tabular-nums whitespace-nowrap"
                          data-testid="pp-progress-label">
                      {t('pages.profile.personal.progress', {
                        completed: completedCount,
                        total: TOTAL_FIELDS,
                        defaultValue: '{{completed}} of {{total}} fields completed',
                      })}
                    </span>
                  </div>
                </div>
              )}
              {/* Datos personales */}
              <section data-testid="profile-personal-group-personal">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('pages.profile.personal.group.personal', { defaultValue: 'Datos personales' })}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pp-birth">{t('pages.profile.personal.birthDate', { defaultValue: 'Fecha de nacimiento' })}</Label>
                    <LocalizedDateInput id="pp-birth" value={profile.birth_date}
                                        onChange={(v) => update('birth_date', v)}
                                        data-testid="pp-birth-date" />
                    {profile.birth_date && (
                      <div className="mt-2 flex items-start gap-2">
                        <input
                          id="pp-birth-consent"
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-pink-600"
                          checked={profile.birthday_share_consent === true}
                          onChange={(e) => update('birthday_share_consent', e.target.checked)}
                          data-testid="pp-birth-consent"
                        />
                        <Label htmlFor="pp-birth-consent" className="text-xs font-normal text-muted-foreground leading-snug">
                          {t('pages.profile.personal.birthdayConsent', {
                            defaultValue: 'Autorizo mostrar mi cumpleaños (día y mes) en el calendario de celebraciones de mi organización.',
                          })}
                        </Label>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="pp-addr">{t('pages.profile.personal.address', { defaultValue: 'Dirección de residencia' })}</Label>
                    <Input id="pp-addr" value={profile.address}
                           onChange={(e) => update('address', e.target.value)}
                           data-testid="pp-address" />
                  </div>
                </div>
              </section>

              {/* Gustos y preferencias */}
              <div className="rounded-lg border border-pink-100 bg-pink-50/40 p-3 mb-3 flex items-start gap-2" data-testid="pp-prefs-consent-block">
                <input
                  id="pp-prefs-consent"
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-pink-600"
                  checked={profile.preferences_share_consent === true}
                  onChange={(e) => update('preferences_share_consent', e.target.checked)}
                  data-testid="pp-prefs-consent"
                />
                <Label htmlFor="pp-prefs-consent" className="text-xs font-normal text-muted-foreground leading-snug">
                  {t('pages.profile.personal.prefsConsent', {
                    defaultValue: 'Autorizo compartir mis gustos y preferencias no sensibles (color, postre, comida, deporte, equipo, pasatiempos) con mi organización para celebraciones y reconocimientos. Puedo retirar esta autorización en cualquier momento. Mi dirección y datos familiares nunca se comparten.',
                  })}
                </Label>
              </div>
              <section data-testid="profile-personal-group-likes">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('pages.profile.personal.group.likes', { defaultValue: 'Gustos y preferencias' })}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pp-color">{t('pages.profile.personal.color', { defaultValue: 'Color favorito' })}</Label>
                    <Input id="pp-color" value={profile.favorite_color}
                           onChange={(e) => update('favorite_color', e.target.value)}
                           data-testid="pp-color" />
                  </div>
                  <div>
                    <Label htmlFor="pp-dessert">{t('pages.profile.personal.dessert', { defaultValue: 'Postre favorito' })}</Label>
                    <Input id="pp-dessert" value={profile.favorite_dessert}
                           onChange={(e) => update('favorite_dessert', e.target.value)}
                           data-testid="pp-dessert" />
                  </div>
                  <div>
                    <Label htmlFor="pp-sport">{t('pages.profile.personal.sport', { defaultValue: 'Deporte favorito' })}</Label>
                    <Input id="pp-sport" value={profile.favorite_sport}
                           onChange={(e) => update('favorite_sport', e.target.value)}
                           data-testid="pp-sport" />
                  </div>
                  <div>
                    <Label htmlFor="pp-team">{t('pages.profile.personal.team', { defaultValue: 'Equipo favorito' })}</Label>
                    <Input id="pp-team" value={profile.favorite_team}
                           onChange={(e) => update('favorite_team', e.target.value)}
                           data-testid="pp-team" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pp-food">{t('pages.profile.personal.food', { defaultValue: 'Comida favorita' })}</Label>
                    <Input id="pp-food" value={profile.favorite_food}
                           onChange={(e) => update('favorite_food', e.target.value)}
                           data-testid="pp-food" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pp-hobbies">{t('pages.profile.personal.hobbies', { defaultValue: 'Hobbies / pasatiempos' })}</Label>
                    <Input id="pp-hobbies" value={hobbiesText}
                           onChange={(e) => setHobbiesText(e.target.value)}
                           placeholder={t('pages.profile.personal.hobbiesPh', { defaultValue: 'Separa con comas: lectura, fotografía, ...' })}
                           data-testid="pp-hobbies" />
                  </div>
                </div>
              </section>

              {/* Familia */}
              <section data-testid="profile-personal-group-family">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('pages.profile.personal.group.family', { defaultValue: 'Familia' })}
                  </h3>
                  <Button type="button" size="sm" variant="outline" onClick={addChild}
                          data-testid="pp-add-child" className="gap-1.5">
                    <UserPlus className="h-4 w-4" />
                    {t('pages.profile.personal.addChild', { defaultValue: 'Agregar hijo' })}
                  </Button>
                </div>
                {profile.children.length === 0 && (
                  <p className="text-xs text-muted-foreground italic" data-testid="pp-no-children">
                    {t('pages.profile.personal.noChildren', { defaultValue: 'Sin hijos registrados.' })}
                  </p>
                )}
                <div className="space-y-3">
                  {profile.children.map((c, i) => (
                    <div key={i} className="grid sm:grid-cols-[110px_140px_1fr_40px] gap-3 items-end border rounded-lg p-3"
                         data-testid={`pp-child-row-${i}`}>
                      <div>
                        <Label htmlFor={`pp-child-age-${i}`}>{t('pages.profile.personal.childAge', { defaultValue: 'Edad' })}</Label>
                        <Input id={`pp-child-age-${i}`} type="number" min={0} max={50}
                               value={c.age} onChange={(e) => setChild(i, 'age', e.target.value)}
                               data-testid={`pp-child-age-${i}`} />
                      </div>
                      <div>
                        <Label>{t('pages.profile.personal.childGender', { defaultValue: 'Género' })}</Label>
                        <Select value={c.gender || ''} onValueChange={(v) => setChild(i, 'gender', v)}>
                          <SelectTrigger data-testid={`pp-child-gender-${i}`}>
                            <SelectValue placeholder={t('pages.profile.personal.childGenderPh', { defaultValue: 'Selecciona' })} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">{t('pages.profile.personal.male', { defaultValue: 'Masculino' })}</SelectItem>
                            <SelectItem value="F">{t('pages.profile.personal.female', { defaultValue: 'Femenino' })}</SelectItem>
                            <SelectItem value="O">{t('pages.profile.personal.other', { defaultValue: 'Otro' })}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`pp-child-int-${i}`}>{t('pages.profile.personal.childInterests', { defaultValue: 'Intereses' })}</Label>
                        <Input id={`pp-child-int-${i}`} value={c.interests}
                               onChange={(e) => setChild(i, 'interests', e.target.value)}
                               data-testid={`pp-child-interests-${i}`} />
                      </div>
                      <Button type="button" size="icon" variant="ghost"
                              onClick={() => removeChild(i)}
                              data-testid={`pp-child-remove-${i}`}
                              aria-label="remove">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </section>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t">
                {isIntro ? (
                  <>
                    <button type="button"
                            onClick={skipForNow}
                            disabled={saving}
                            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline self-start sm:self-auto"
                            data-testid="pp-skip-later">
                      {t('pages.profile.personal.skipLater', { defaultValue: 'Completar más tarde' })}
                    </button>
                    <Button onClick={continueToEvals} disabled={saving}
                            className="bg-gradient-primary hover:opacity-90"
                            data-testid="pp-continue">
                      {saving
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving', { defaultValue: 'Guardando…' })}</>
                        : t('pages.profile.personal.continueToEvals', { defaultValue: 'Continuar a mis evaluaciones' })}
                    </Button>
                  </>
                ) : (
                  <>
                    <span />
                    <Button onClick={save} disabled={saving}
                            className="bg-gradient-primary hover:opacity-90"
                            data-testid="pp-save">
                      {saving
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving', { defaultValue: 'Guardando…' })}</>
                        : t('pages.profile.personal.save', { defaultValue: 'Guardar' })}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
